import { createClient } from '@supabase/supabase-js';

const supabase = createClient(
  import.meta.env.VITE_SUPABASE_URL!,
  import.meta.env.VITE_SUPABASE_ANON_KEY!
);

const BUCKET_NAME = import.meta.env.VITE_STORAGE_BUCKET || 'location-images';
const VALID_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const validateImageFile = (file: File): void => {
  if (!file || !(file instanceof File)) {
    throw new Error('Невалідны файл');
  }

  // Check if the file is actually an image by reading its header
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const arr = new Uint8Array(e.target?.result as ArrayBuffer).subarray(0, 4);
      let header = '';
      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }
      
      // Check magic numbers for common image formats
      const isValidImage = (
        header.startsWith('89504e47') || // PNG
        header.startsWith('ffd8') || // JPEG
        header.startsWith('47494638') // GIF
      );
      
      if (!isValidImage) {
        reject(new Error('Файл не з\'яўляецца выявай'));
        return;
      }
      
      if (!VALID_MIME_TYPES.includes(file.type)) {
        reject(new Error('Недапушчальны тып файла. Дазволены толькі JPEG, PNG, GIF і WebP.'));
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        reject(new Error('Файл занадта вялікі. Максімальны памер - 5MB.'));
        return;
      }
      
      resolve();
    };
    reader.onerror = () => reject(new Error('Памылка чытання файла'));
    reader.readAsArrayBuffer(file.slice(0, 4));
  });
};

export const getImageUrl = (imagePath: string): string => {
  if (imagePath.startsWith('http')) {
    return imagePath;
  }
  return `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/${BUCKET_NAME}/${imagePath}`;
};

export const uploadImage = async (file: File): Promise<string> => {
  try {
    validateImageFile(file);

    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 8);
    const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
    const fileName = `${timestamp}_${random}.${ext}`;

    const { data, error } = await supabase
      .storage
      .from(BUCKET_NAME)
      .upload(fileName, file, {
        cacheControl: '3600',
        contentType: file.type,
        upsert: false
      });

    if (error) {
      throw new Error(`Памылка загрузкі файла: ${error.message}`);
    }

    if (!data?.path) {
      throw new Error('Не атрымалася атрымаць шлях да файла');
    }

    return data.path;
  } catch (error) {
    console.error('Error uploading image:', error);
    throw error;
  }
};

export const uploadImages = async (files: File[]): Promise<string[]> => {
  try {
    const uploadPromises = files.map(file => uploadImage(file));
    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error('Error uploading images:', error);
    throw error;
  }
};